import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['content', 'header', 'body', 'footer']

  connect() {
    // Focus management (WCAG 2.4.3): remember what was focused, move focus into
    // the dialog, and trap Tab within it while open. Only real dialogs get this
    // treatment — some pages attach this controller to a plain page wrapper,
    // where trapping focus would wrongly hijack keyboard navigation.
    if (this.#isDialog) {
      this.#previouslyFocused = document.activeElement
      this.#trapHandler = this.#trapFocus.bind(this)
      this.element.addEventListener('keydown', this.#trapHandler)
      this.#focusInitial()
    }

    if (this.hasBodyTarget) {
      this.#handleScroll = this.#updateScrollBorders.bind(this)
      this.bodyTarget.addEventListener('scroll', this.#handleScroll)

      this.#resizeObserver = new ResizeObserver(this.#handleScroll)
      this.#resizeObserver.observe(this.bodyTarget)

      this.#updateScrollBorders()
    }
  }

  disconnect() {
    if (this.hasBodyTarget) {
      this.bodyTarget.removeEventListener('scroll', this.#handleScroll)
    }
    this.#resizeObserver?.disconnect()

    if (this.#trapHandler) {
      this.element.removeEventListener('keydown', this.#trapHandler)
      // Restore focus to the element that opened the modal.
      if (this.#previouslyFocused?.isConnected) {
        this.#previouslyFocused.focus()
      }
    }
  }

  close() {
    this.element.parentElement.removeAttribute('src')
    this.element.remove()
  }

  backdropClose(event) {
    if (!event.target.isConnected) return
    if (!this.contentTarget.contains(event.target)) {
      this.close()
    }
  }

  // Private

  #handleScroll
  #resizeObserver
  #previouslyFocused
  #trapHandler

  get #isDialog() {
    return this.element.getAttribute('role') === 'dialog'
  }

  #focusContainer() {
    return this.hasContentTarget ? this.contentTarget : this.element
  }

  #focusableElements() {
    const selector = [
      'a[href]:not([tabindex="-1"])', 'button:not([disabled])', 'textarea:not([disabled])',
      'input:not([disabled]):not([type="hidden"])', 'select:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',')
    return Array.from(this.#focusContainer().querySelectorAll(selector))
      .filter((el) => el.offsetParent !== null)
  }

  #focusInitial() {
    const focusable = this.#focusableElements()
    const autofocused = focusable.find((el) => el.hasAttribute('autofocus'))
    if (focusable.length) {
      (autofocused || focusable[0]).focus()
    } else {
      const container = this.#focusContainer()
      container.setAttribute('tabindex', '-1')
      container.focus()
    }
  }

  #trapFocus(event) {
    if (event.key !== 'Tab') return

    const focusable = this.#focusableElements()
    if (!focusable.length) {
      event.preventDefault()
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    }
  }

  #updateScrollBorders() {
    const body = this.bodyTarget
    const scrolledDown = body.scrollTop > 0
    const scrolledToBottom = body.scrollTop + body.clientHeight >= body.scrollHeight - 1

    if (this.hasHeaderTarget) {
      this.headerTarget.classList.toggle('border-contrast-low', scrolledDown)
      this.headerTarget.classList.toggle('border-transparent', !scrolledDown)
    }

    if (this.hasFooterTarget) {
      const hasOverflow = body.scrollHeight > body.clientHeight
      const showBorder = hasOverflow && !scrolledToBottom

      this.footerTarget.classList.toggle('border-contrast-low', showBorder)
      this.footerTarget.classList.toggle('border-transparent', !showBorder)
    }
  }
}
